//-----------------------------------------------------------------------
// Copyright © 2022 ManpowerGroup. All Rights Reserved.
//-----------------------------------------------------------------------

/* eslint-disable prettier/prettier */
import React, { useState, useEffect, useContext } from "react";
import { Helmet } from "react-helmet-async";
import * as Yup from "yup";
import { Formik } from "formik";
import { Card, Col, Container, Form, Row, Button } from "react-bootstrap";
import { SystemMessageActions } from './messageAction';
import moment from 'moment-timezone';
import DateTime from "react-datetime";
import { Context } from "../../src/common/context/Context";
import { MultiSelect } from "react-multi-select-component";
import AlertMessage from "../common/alertMessages/alertMessages";
import ErrorAlertMessage from "../common/alertMessages/errorAlertMessage";
import {
  getLabelFromCmsComponent,
  getSystemMessageCmsFetchers,
} from "./systemMessageCmsHelper";
import {
  extractIanaTimezones,
  updateSelectedTimeZone,
  isFutureTime,
} from "./systemMessageTimeHelper";
const MessageForm = ({ candidateCMSData }) => {
  const context = useContext(Context);
  const [countriesJson, setCountriesJson] = useState([]);
  const [timezoneJson, setTimezoneJson] = useState([]);
  const [alert, setAlert] = useState({
    message: "",
    type: "",
    showAlert: false
  })
  const [errorAlert, setErrorAlert] = useState({
    showAlert: false
  })
  const [disableOnPublish, setDisableOnPublish] = useState(false);
  const {
    fetchSystemMessageLabel,
    fetchCommonComponentLabel,
    fetchSystemMessageValidationLabel,
  } = getSystemMessageCmsFetchers(candidateCMSData);


  const fetchCountriesJson = async () => {
    let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));

    await SystemMessageActions.GetCountriesJson(userId).then(
      (res) => {
        if (res.status === 200 || res.status === 204) {
          const countriesCodeRes = JSON.parse(
            JSON.stringify(res?.data.countryLocaleCodes),
          );
          const countries = [];
          countriesCodeRes.forEach((data) => {
            countries.push({
              value: data,
              label: data.masterCountryName + "   " + data.localeName,
            });
          });
          setCountriesJson(countries);
        } else {
          refErrorMessageAlert.showAlert = true;
          setErrorAlert(refErrorMessageAlert);
          window.scrollTo(0, 0);
        }
      },
      (err) => {
        console.log(err);
      },
    );
  };

  const fetchTimezoneJson = async (language) => {
    let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));

    await SystemMessageActions.GetTimezoneJson(language).then(
      (res) => {
        if (res.status === 200 || res.status === 204) {
          const timezoneRes = JSON.parse(JSON.stringify(res?.data?.[0]?.items || []));
          const timezones = extractIanaTimezones(timezoneRes);
          setTimezoneJson(timezones);
        }
        else {
          refErrorMessageAlert.showAlert = true;
          setErrorAlert(refErrorMessageAlert);
          window.scrollTo(0, 0);
        }
      },
      (err) => {
        console.log(err);
      }
    );
  }

  useEffect(() => {
    fetchCountriesJson();
    if ((context?.userDetails?.language !== undefined) && (context?.userDetails?.language !== null)) {
      fetchTimezoneJson(context.userDetails.language);
    }
  }, []);


  const publishMessage = async (data, resetForm, setSubmit) => {
    setDisableOnPublish(true);
    let refAlert = JSON.parse(JSON.stringify(alert));

    await SystemMessageActions.PostPublishMessage(data).then(
      (res) => {
        if (res.status === 200) {
          resetForm();
          setSubmit();
          refAlert.showAlert = true;
          refAlert.type = "success";
          refAlert.message = fetchSystemMessageLabel('SystemMessage.PublishSuccessLabel');
        } else if (res.status === 500 || res.status === 400 || res.status === 401 || res.status === 404 || res.status === 403) {
          refAlert.showAlert = true;
          refAlert.type = "danger";
          refAlert.message = fetchSystemMessageLabel('SystemMessage.PublishErrorLabel');
        } else if (res.status === 204) {
          refAlert.showAlert = true;
          refAlert.type = "danger";
          refAlert.message = res ? res : fetchSystemMessageLabel('SystemMessage.PublishNoResponseLabel');
        }
        setAlert(refAlert)
        setDisableOnPublish(false);
      },
      (err) => {
        console.log(err);
      }
    );
  }

  const showModal = () => {
    let refAlert = JSON.parse(JSON.stringify(alert));
    refAlert.showAlert = false;
    setAlert(refAlert)
  }

  const showErrorModal = () => {
    let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));
    refErrorMessageAlert.showAlert = false;
    setErrorAlert(refErrorMessageAlert)
  }

  let userId = JSON.parse(window.sessionStorage.getItem('UserAccount'))?.["accountId"];
  return (
    <Formik
      initialValues={{
        country: [],
        language: "",
        messageHeader: "",
        messageContent: "",
        persistent: false,
        timezone: "",
        fromDate: "",
        fromDay: "",
        fromTime: "",
        toDate: "",
        toDay: "",
        toTime: "",
        validDate: false,
      }}
      validationSchema={Yup.object().shape({
        messageHeader: Yup.string()
          .max(60)
          .required(fetchSystemMessageValidationLabel('SystemMessage.headerValidationMessage')),
        messageContent: Yup.string().max(700).required(fetchSystemMessageValidationLabel('SystemMessage.contentValidationMessage')),
        timezone: Yup.string().required(fetchSystemMessageValidationLabel('SystemMessage.timezoneValidationMessage')),
      })}
      onReset={(values, { resetForm }) => {

      }}
      onSubmit={(values, { resetForm, setSubmitting }) => {
        const countryCodes = values?.country?.map(item => ({ countryCode: item.value.countryIdentifier, languageCode: item.value.localeName }))

        const data = {
          messageTitle: values.messageHeader,
          messageDescription: values.messageContent,
          sentBy: userId,
          fromDate: updateSelectedTimeZone(values.timezone, values.fromDate)?.utc().format('YYYY-MM-DDTHH:mm:ssZ'),
          toDate: updateSelectedTimeZone(values.timezone, values.toDate)?.utc().format('YYYY-MM-DDTHH:mm:ssZ'),
          countryLanguageCodes: countryCodes,
          isPersistent: values.persistent,
          timezone: values.timezone
        }

        publishMessage(data, resetForm, setSubmitting);


      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        handleReset,
        isSubmitting,
        touched,
        values,
        setFieldValue,
      }) => (
        <Form onSubmit={handleSubmit} onReset={handleReset}>
          {errorAlert?.showAlert &&
            <ErrorAlertMessage alertMessage="AllPages.ErrorAlertMessage" showAlert={showErrorModal} />
          }
          <Form.Group className="mb-3">
            <Row>
              <Col lg="2">{fetchSystemMessageLabel('SystemMessage.countryLabel')}</Col>
              <Col lg="4">
                <MultiSelect
                  options={countriesJson}
                  value={values.country}
                  onChange={(data) => {
                    data.length ? setFieldValue("country", data) : handleReset();
                  }}
                  showSelectAll={true}
                  closeOnChangedValue={true}
                  labelledBy="Select Country"
                />
              </Col>
            </Row>
            {alert?.showAlert &&
              <AlertMessage alertMessage={alert?.message} alertType={alert?.type} showAlert={showModal} />
            }
          </Form.Group>

          {(values?.country?.length > 0) && (
            <div>
              <Form.Group className="mb-3">
                <Row className='mt-3'>
                  <Col lg="2">{fetchSystemMessageLabel('SystemMessage.PlaceHolder.MessageHeader')}</Col>
                  <Col lg="4">
                    <Form.Control type="text" name="messageHeader" maxLength={60}
                      placeholder={fetchSystemMessageLabel('SystemMessage.PlaceHolder.MessageHeader')}
                      value={values.messageHeader}
                      isInvalid={Boolean(touched.messageHeader && errors.messageHeader)}
                      onBlur={handleBlur}
                      onChange={handleChange} />
                    {!!touched.messageHeader && (
                      <Form.Control.Feedback type="invalid">
                        {errors.messageHeader}
                      </Form.Control.Feedback>
                    )}
                  </Col>
                </Row>
              </Form.Group>

              <Form.Group className="mb-3">
                <Row className='mt-3'>
                  <Col lg="2">{fetchSystemMessageLabel('SystemMessage.messageContentLabel')}</Col>
                  <Col lg="4">
                    <Form.Control as="textarea" name="messageContent" maxLength={700}
                      placeholder={fetchSystemMessageLabel('SystemMessage.PlaceHolder.MessageContent')}
                      value={values.messageContent}
                      isInvalid={Boolean(touched.messageContent && errors.messageContent)}
                      onBlur={handleBlur}
                      onChange={handleChange} />
                    {!!touched.messageContent && (
                      <Form.Control.Feedback type="invalid">
                        {errors.messageContent}
                      </Form.Control.Feedback>
                    )}
                  </Col>
                </Row>
              </Form.Group>

              <Form.Group className="mb-3">
                <Row className='mt-3'>
                  <Col lg="2">{fetchSystemMessageLabel('SystemMessage.persistentLabel')}</Col>
                  <Col lg="4">
                    <Form.Check
                      name='persistent'
                      checked={values.persistent}
                      type="checkbox"
                      onChange={(data) => {
                        setFieldValue("persistent", data.target.checked);
                      }}
                      aria-label="Persistent Check"
                    />
                  </Col>
                </Row>
              </Form.Group>

              <Form.Group className="mb-3">
                <Row className='mt-3'>
                  <Col lg="2"> <Form.Label>{fetchSystemMessageLabel('SystemMessage.timezoneLabel')}</Form.Label></Col>
                  <Col lg="4"> <Form.Select name="timeZone" value={values.timezone} aria-label="TimeZone Select"
                    isInvalid={Boolean(touched.timezone && errors.timezone)}
                    onChange={e => {
                      setFieldValue("timezone", e.target.value);
                    }}>
                    <option value=''>{fetchSystemMessageLabel('SystemMessage.timezoneOption')}</option>
                    {timezoneJson.map((item) => (
                      <option key={item?.title} value={item?.standardCodeValue}>
                        {item?.title}
                      </option>
                    ))}
                  </Form.Select>
                    {!!touched.timezone && (<div className="invalid-feedback">{errors.timezone}</div>)}
                  </Col>
                </Row>
              </Form.Group>

              <Form.Group className="mb-3">
                <Row className='mt-3'>
                  <Col lg="2">{fetchSystemMessageLabel('SystemMessage.fromDateLabel')}</Col>
                  <Col lg="2" className="mb-2">
                    <DateTime
                      dateFormat="DD/MM/YYYY"
                      value={values.fromDay}
                      onChange={(data) => {
                        setFieldValue("fromDay", moment(data).format('DD/MM/YYYY'));
                        setFieldValue("fromTime", "12:00 AM");
                        setFieldValue("fromDate", moment(moment(data).format('DD/MM/YYYY') + ' ' +
                          moment("12:00 AM", ["h:mm A"]).format("HH:mm"), 'DD/MM/YYYY HH:mm:ss').format());
                      }}
                      closeOnSelect={true}
                      timeFormat={false}
                      isValidDate={current => current.isAfter(moment().subtract(1, 'day'))}
                      inputProps={{
                        disabled: values.timezone === '',
                        'aria-label': 'from date',
                      }}
                    />
                  </Col>
                  <Col lg="2">
                    <DateTime
                      dateFormat={false}
                      timeFormat={true}
                      viewMode="time"
                      closeOnSelect={true}
                      value={values.fromTime}
                      onChange={(data) => {
                        setFieldValue("fromTime", data);
                        setFieldValue("fromDate",
                          moment(values.fromDay + ' ' +
                            moment(data, ["h:mm A"]).format("HH:mm"), 'DD/MM/YYYY HH:mm:ss').format());
                      }}
                      inputProps={{
                        disabled: values.timezone === '',
                        'aria-label': 'from time'
                      }}
                    />
                  </Col>
                  <Col lg="4">
                    {values.fromDate && isFutureTime(values.timezone, values.fromDate) &&
                      <div class="invalid-feedback" style={{ display: "block" }}>{fetchSystemMessageValidationLabel('SystemMessage.fromDateValidationError')}</div>}
                  </Col>
                </Row>
              </Form.Group>

              <Form.Group className="mb-3">
                <Row className='mt-3'>
                  <Col lg="2">{fetchSystemMessageLabel('SystemMessage.toDateLabel')}</Col>
                  <Col lg="2" className="mb-2">
                    <DateTime
                      dateFormat="DD/MM/YYYY"
                      value={values.toDay}
                      onChange={(data) => {
                        setFieldValue("toDay", moment(data).format('DD/MM/YYYY'));
                        setFieldValue("toTime", "12:00 AM");
                        setFieldValue("toDate",
                          moment(moment(data).format('DD/MM/YYYY') + ' ' +
                            moment("12:00 AM", ["h:mm A"]).format("HH:mm"), 'DD/MM/YYYY HH:mm:ss').format())
                      }}
                      closeOnSelect={true}
                      timeFormat={false}
                      isValidDate={current => current.isAfter(moment().subtract(1, 'day'))}
                      inputProps={{
                        disabled: values.timezone === '',
                        'aria-label': 'to date'
                      }}
                    />
                  </Col>
                  <Col lg="2">
                    <DateTime
                      dateFormat={false}
                      timeFormat={true}
                      viewMode="time"
                      closeOnSelect={true}
                      value={values.toTime}
                      onChange={(data) => {
                        setFieldValue("toTime", data);
                        setFieldValue("toDate",
                          moment(values.toDay + ' ' + moment(data, ["h:mm A"]).format("HH:mm"), 'DD/MM/YYYY HH:mm:ss').format())
                      }}
                      inputProps={{
                        disabled: values.timezone === '',
                        'aria-label': 'to time'
                      }}
                    />
                  </Col>
                  <Col lg="4">
                    {((values.toDate && isFutureTime(values.timezone, values.toDate)) || (Date.parse(values?.fromDate) > Date.parse(values?.toDate))) &&
                      <div class="invalid-feedback" style={{ display: "block" }}>{fetchSystemMessageValidationLabel('SystemMessage.toDateValidationError')}</div>}
                  </Col>
                </Row>
              </Form.Group>

              <Row className="mt-3">
                <Col lg="4" className='text-center mb-2'>
                  <Button
                    type="reset"
                    variant="primary"
                    size="lg"
                  >
                    {fetchCommonComponentLabel('Common.Cancel')}
                  </Button>
                </Col>
                <Col lg="4" className='text-center'>
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    disabled={(isSubmitting && values.validDate) ||
                      !values.fromDate || !values.toDate ||
                      isFutureTime(values?.timezone, values.toDate) ||
                      isFutureTime(values?.timezone, values.fromDate) ||
                      (Date.parse(values.fromDate) > Date.parse(values.toDate)) || disableOnPublish}
                  >
                    {fetchSystemMessageLabel('SystemMessage.Add.Button.Publish')}
                  </Button>
                </Col>
              </Row>
            </div>)}
        </Form>
      )}
    </Formik>
  )
}

const SystemMessage = ({ candidateCMSData }) => {
  const fetchSystemMessageLabel = (key) =>
    getLabelFromCmsComponent(
      candidateCMSData?.pageContent["Candidates.SystemMessage"],
      key
    );

  return (
    <React.Fragment>
      <Helmet title="System Message" />
      <Container fluid className="p-0">
        <h1 className="h3 mb-3">{fetchSystemMessageLabel('SystemMessage.addSystemMessage')}</h1>
        <Card>
          <Card.Body>
            <MessageForm candidateCMSData={candidateCMSData} />
          </Card.Body>
        </Card>
      </Container>
    </React.Fragment>
  )
}


export default SystemMessage;
